import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  FiSave,
  FiPlus,
  FiTrash2,
  FiMusic,
  FiUsers,
  FiDisc,
  FiCalendar,
  FiImage,
  FiAward,
  FiExternalLink,
  FiChevronDown,
} from "react-icons/fi";

import AdminContainer from "../../../components/utils/AdminContainer";

import {
  getBandDetails,
  editBand,
} from "../../../store/slices/bands/asyncThunks";

import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  TabNavigation,
  TabButton,
  ContentSection,
  FormGrid,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  Card,
  CardHeader,
  CardContent,
  ItemCard,
  EmptyState,
  SaveButton,
  GalleryGrid,
  GalleryItemCard,
  GalleryItemHeader,
  GalleryItemControls,
  MainImageBadge,
  GalleryRemoveButton,
  ImagePreview,
  PreviewImage,
  PlaceholderImage,
  FileUploadInput,
  CheckboxLabel,
  AchievementsList,
  AchievementItem,
  AchievementInput,
  AchievementYearInput,
  AchievementRemoveButton,
  BackLink,
  Relative,
  TracksList,
  TrackItem,
  TrackGrid,
  TrackRemoveButton,
  AlbumSection,
  TracksSection,
  SectionDivider,
} from "./AdminBandDetailsPage.styles";
import { FaArrowLeft, FaRegCalendarAlt } from "react-icons/fa";
import { toast } from "../../../components/modern-ui/sonner";

const ensureArray = (value, defaultValue = []) => {
  if (Array.isArray(value)) return value;
  if (!value) return defaultValue;
  return defaultValue;
};

const ensureObject = (value, defaultValue = {}) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  return defaultValue;
};

const AdminBandDetailsPage = () => {
  const { bandId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("biography");
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  const [bandData, setBandData] = useState({
    id: bandId,
    name: "",
    genre: "",
    description: "",
    city: "",
    founded: "",
    biography: "",
    members: [],
    discography: [],
    events: [],
    gallery: [],
    achievements: [],
    socialLinks: {
      spotify: "",
      youtube: "",
      instagram: "",
      facebook: "",
      twitter: "",
      apple: "",
      soundcloud: "",
      website: "",
    },
    stats: {
      albums: 0,
      years: 0,
      numOfProducts: 0,
      numOfNews: 0,
    },
  });

  useEffect(() => {
    loadBandDetails();
  }, [bandId]);

  const loadBandDetails = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getBandDetails(bandId)).unwrap();

      const safeData = response.data || response || {};

      const formattedData = {
        id: safeData.id || bandId,
        name: safeData.name || "",
        genre: safeData.genre || "",
        city: safeData.city || "",
        founded: safeData.founded || "",
        biography: safeData.biography || "",
        description: safeData.description || "",

        //  Koristi members direktno (već parsirani na backend strani)
        members: ensureArray(safeData.members).map((member, index) => ({
          id: member?.id || Date.now() + index,
          name: String(member?.name || ""),
          role: String(member?.role || ""),
          bio: String(member?.bio || ""),
          joined: parseInt(
            member?.joined || safeData.founded || new Date().getFullYear()
          ),
        })),

        //  Koristi discography direktno (već parsirani na backend strani)
        discography: ensureArray(safeData.discography).map((album, index) => ({
          id: album?.id || Date.now() + index,
          title: String(album?.title || ""),
          year: parseInt(album?.year) || new Date().getFullYear(),
          type: String(album?.type || "Studio album"),
          tracks: ensureArray(album?.tracks).map((track, trackIndex) => ({
            id: track?.id || Date.now() + trackIndex + Math.random(),
            name: String(track?.name || track?.title || ""),
            duration: String(track?.duration || ""),
          })),
        })),

        //  Koristi events direktno (već parsirani na backend strani)
        events: ensureArray(safeData.events).map((event, index) => ({
          id: event?.id || Date.now() + index,
          date: event?.date || "",
          venue: event?.venue || "",
          city: event?.city || "",
          country: event?.country || "",
          ticketUrl: event?.ticketUrl || "",
          status: event?.status || "available",
        })),

        gallery: ensureArray(safeData.bandImages).map((image, index) => ({
          id: image?.id || Date.now() + index,
          file: null,
          url: image?.url || "",
          filename: image?.filename || "",
          originalName: image?.originalName || "",
          isMain: Boolean(image?.isMain),
          isBanner: Boolean(image?.isBanner),
          existing: true, // Ključno za backend handling
        })),

        achievements: ensureArray(safeData.achievements).map(
          (achievement, index) => {
            if (typeof achievement === "string") {
              return {
                id: Date.now() + index,
                title: achievement,
                year: new Date().getFullYear(),
              };
            }
            return {
              id: achievement.id || Date.now() + index,
              title: String(achievement.title || achievement.name || ""),
              year: parseInt(achievement.year) || new Date().getFullYear(),
            };
          }
        ),

        //  Koristi socialLinks direktno (već transformisani na backend strani)
        socialLinks: ensureObject(safeData.socialLinks, {
          spotify: "",
          youtube: "",
          instagram: "",
          facebook: "",
          twitter: "",
          apple: "",
          soundcloud: "",
          website: "",
        }),

        //  Koristi stats direktno (već parsirani na backend strani)
        stats: ensureObject(safeData.stats, {
          albums: 0,
          years: 0,
          numOfProducts: 0,
          numOfNews: 0,
        }),

        // Admin fields
        isFeatured: Boolean(safeData.isFeatured),
        active: safeData.active !== false,
        label: safeData.label || "",
        status: safeData.status || "Aktivan",
      };

      setBandData(formattedData);
    } catch (error) {
      console.error("Error loading band details:", error);
      toast.error(
        "Greška pri učitavanju detalja benda: " +
          (error.message || "Neočekivana greška")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value, section = null) => {
    if (section) {
      setBandData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setBandData((prev) => ({ ...prev, [field]: value }));
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Filtriraj prazne URL-ove pre slanja
      const filteredSocialLinks = {};
      Object.keys(bandData.socialLinks).forEach((key) => {
        const value = bandData.socialLinks[key];
        if (value && value.trim() !== "") {
          filteredSocialLinks[key] = value.trim();
        }
      });

      // Filtriraj events i ukloni prazne ticketUrl-ove
      const filteredEvents = bandData.events.map((event) => {
        const cleanEvent = { ...event };

        // Ukloni ticketUrl ako je prazan
        if (!cleanEvent.ticketUrl || cleanEvent.ticketUrl.trim() === "") {
          delete cleanEvent.ticketUrl;
        }

        return cleanEvent;
      });

      // Očisti diskografiju i tracks pre slanja
      const cleanedDiscography = bandData.discography.map((album) => ({
        ...album,
        year: parseInt(album.year) || new Date().getFullYear(),
        tracks: album.tracks
          .filter((track) => track.name.trim() !== "") // Ukloni prazne tracks
          .map((track) => ({
            id: track.id,
            name: track.name.trim(),
            duration: track.duration.trim(),
          })),
      }));

      // Helper funkcija za čišćenje slika
      const cleanImage = (image) => ({
        id: image.id,
        filename: image.filename,
        originalName: image.originalName || image.filename,
        url: image.url,
        isMain: Boolean(image.isMain),
        isBanner: Boolean(image.isBanner),
        existing: true,
      });

      // Razdvoj uploadovane i postojeće slike
      const uploadedImages = bandData.gallery.filter((image) => image.file);
      const existingImages = bandData.gallery.filter(
        (image) =>
          !image.file && image.url && !image.url.startsWith("data:image/")
      );

      // Očisti postojeće gallery slike
      const cleanedGallery = existingImages
        .filter((image) => image.url && image.url.length < 255) // Samo URL-ovi kraći od 255 karaktera
        .map(cleanImage);

      // Ako ima uploadovanih slika, poslaj ih kao fajlove sa indeksima
      let formData = null;
      if (uploadedImages.length > 0) {
        formData = new FormData();

        // Dodaj osnovne podatke (eksplicitno ukloni polja koja se posebno obrađuju)
        Object.keys(bandData).forEach((key) => {
          if (
            key !== "gallery" &&
            key !== "socialLinks" &&
            key !== "events" &&
            key !== "discography" &&
            key !== "members" &&
            key !== "achievements" &&
            key !== "subscriptionPackage" &&
            key !== "description" &&
            key !== "biography" &&
            key !== "stats" // Isključi stats objekat
          ) {
            const value = bandData[key];
            if (typeof value === "object" && value !== null) {
              console.log(`Skipping object field: ${key}`, value);
            } else if (value !== undefined && value !== null) {
              formData.append(key, value);
            }
          }
        });

        // Dodaj achievements kao niz
        const cleanAchievements = Array.isArray(bandData.achievements)
          ? bandData.achievements
              .filter(
                (achievement) =>
                  achievement &&
                  achievement.title &&
                  achievement.title.trim() !== ""
              )
              .map((achievement) => ({
                ...achievement,
                year: parseInt(achievement.year) || new Date().getFullYear(),
              }))
          : [];
        formData.append("achievements", JSON.stringify(cleanAchievements));

        // Dodaj members kao niz
        const cleanMembers = Array.isArray(bandData.members)
          ? bandData.members.map((member) => ({
              ...member,
              joined:
                parseInt(member.joined) ||
                bandData.founded ||
                new Date().getFullYear(),
            }))
          : [];
        formData.append("members", JSON.stringify(cleanMembers));

        // Dodaj description i biography kao stringovi
        if (bandData.description && typeof bandData.description === "string") {
          formData.append("description", bandData.description);
        }
        if (bandData.biography && typeof bandData.biography === "string") {
          formData.append("biography", bandData.biography);
        }
        // Dodaj social links
        if (filteredSocialLinks.facebook)
          formData.append("fbUrl", filteredSocialLinks.facebook);
        if (filteredSocialLinks.youtube)
          formData.append("ytUrl", filteredSocialLinks.youtube);
        if (filteredSocialLinks.instagram)
          formData.append("instaUrl", filteredSocialLinks.instagram);
        if (filteredSocialLinks.spotify)
          formData.append("spotifyUrl", filteredSocialLinks.spotify);
        if (filteredSocialLinks.twitter)
          formData.append("xUrl", filteredSocialLinks.twitter);
        if (filteredSocialLinks.apple)
          formData.append("appleMusicUrl", filteredSocialLinks.apple);
        if (filteredSocialLinks.soundcloud)
          formData.append("soundcloudUrl", filteredSocialLinks.soundcloud);
        if (filteredSocialLinks.website)
          formData.append("websiteUrl", filteredSocialLinks.website);

        formData.append("events", JSON.stringify(filteredEvents));
        formData.append("discography", JSON.stringify(cleanedDiscography));

        // Dodaj postojeće slike ako postoje
        if (cleanedGallery.length > 0) {
          formData.append("images", JSON.stringify(cleanedGallery));
        }

        // Dodaj fajlove i pronađi indekse
        let mainImageIndex = -1;
        let bannerImageIndex = -1;

        uploadedImages.forEach((image, index) => {
          formData.append("bandImages", image.file);
          if (image.isMain) mainImageIndex = index;
          if (image.isBanner) bannerImageIndex = index;
        });

        // Dodaj indekse
        formData.append("mainImageIndex", mainImageIndex.toString());
        formData.append("bannerImageIndex", bannerImageIndex.toString());

        for (let pair of formData.entries()) {
          if (
            pair[0] === "members" ||
            pair[0] === "achievements" ||
            pair[0] === "events" ||
            pair[0] === "discography"
          ) {
            console.log(`${pair[0]}:`, JSON.parse(pair[1]));
          } else {
            console.log(`${pair[0]}:`, pair[1]);
          }
        }
      }

      // Ako ima uploadovanih slika, koristi FormData, inače JSON
      if (formData) {
        // Za uploadovane slike - koristi FormData
        await dispatch(
          editBand({ id: bandId, requestData: formData, isFormData: true })
        ).unwrap();
      } else {
        // Za postojeće slike - koristi JSON
        const {
          subscriptionPackage,
          socialLinks,
          fbUrl,
          ytUrl,
          instaUrl,
          spotifyUrl,
          xUrl,
          appleMusicUrl,
          soundcloudUrl,
          websiteUrl,
          ...bandDataWithoutPackage
        } = bandData;
        // Dodaj samo neprazne social media URL-ove sa ispravnim nazivima
        const socialMediaUrls = {};
        if (filteredSocialLinks.facebook)
          socialMediaUrls.fbUrl = filteredSocialLinks.facebook;
        if (filteredSocialLinks.youtube)
          socialMediaUrls.ytUrl = filteredSocialLinks.youtube;
        if (filteredSocialLinks.instagram)
          socialMediaUrls.instaUrl = filteredSocialLinks.instagram;
        if (filteredSocialLinks.spotify)
          socialMediaUrls.spotifyUrl = filteredSocialLinks.spotify;
        if (filteredSocialLinks.twitter)
          socialMediaUrls.xUrl = filteredSocialLinks.twitter;
        if (filteredSocialLinks.apple)
          socialMediaUrls.appleMusicUrl = filteredSocialLinks.apple;
        if (filteredSocialLinks.soundcloud)
          socialMediaUrls.soundcloudUrl = filteredSocialLinks.soundcloud;
        if (filteredSocialLinks.website)
          socialMediaUrls.websiteUrl = filteredSocialLinks.website;

        const saveData = {
          ...bandDataWithoutPackage,
          ...socialMediaUrls,
          events: filteredEvents,
          images: cleanedGallery,
          biography: bandData.biography,
          description: bandData.description,
          discography: cleanedDiscography,
          members: Array.isArray(bandData.members)
            ? bandData.members.map((member) => ({
                ...member,
                joined:
                  parseInt(member.joined) ||
                  bandData.founded ||
                  new Date().getFullYear(),
              }))
            : [],
          achievements: Array.isArray(bandData.achievements)
            ? bandData.achievements
                .filter(
                  (achievement) =>
                    achievement &&
                    achievement.title &&
                    achievement.title.trim() !== ""
                )
                .map((achievement) => ({
                  ...achievement,
                  year: parseInt(achievement.year) || new Date().getFullYear(),
                }))
            : [],
        };

        await dispatch(
          editBand({ id: bandId, requestData: saveData })
        ).unwrap();
      }

      toast.success("Detalji benda su uspešno ažurirani!");
      setHasChanges(false);
    } catch (error) {
      console.error("Save error:", error);
      toast.error(
        "Greška pri čuvanju: " + (error.message || "Neočekivana greška")
      );
    } finally {
      setLoading(false);
    }
  };

  // Gallery handlers
  const addGalleryImage = () => {
    const newImage = {
      id: Date.now(),
      file: null,
      url: "",
      isMain: false,
      isBanner: false,
    };
    setBandData((prev) => ({
      ...prev,
      gallery: [...prev.gallery, newImage],
    }));
    setHasChanges(true);
  };

  const removeGalleryImage = (id) => {
    setBandData((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((image) => image.id !== id),
    }));
    setHasChanges(true);
  };

  const setMainImage = (id) => {
    setBandData((prev) => ({
      ...prev,
      gallery: prev.gallery?.map((image) => ({
        ...image,
        isMain: image.id === id ? true : false,
      })),
    }));
    setHasChanges(true);
  };

  const setBannerImage = (id) => {
    console.log(`Setting banner image with id: ${id}`);
    setBandData((prev) => ({
      ...prev,
      gallery: prev.gallery?.map((image) => ({
        ...image,
        isBanner: image.id === id ? true : false,
      })),
    }));
    setHasChanges(true);
  };

  // Members handlers
  const addMember = () => {
    console.log("Adding new member");
    const newMember = {
      id: Date.now(),
      name: "",
      role: "",
      bio: "",
      joined: new Date().getFullYear(),
    };
    console.log("New member:", newMember);
    setBandData((prev) => {
      const updatedMembers = [...prev.members, newMember];
      console.log("Updated members:", updatedMembers);
      return {
        ...prev,
        members: updatedMembers,
      };
    });
    setHasChanges(true);
  };

  const updateMember = (id, field, value) => {
    console.log(`Updating member ${id}, field: ${field}, value:`, value);
    setBandData((prev) => {
      const updatedMembers = prev.members?.map((member) =>
        member.id === id ? { ...member, [field]: value } : member
      );
      console.log("Updated members:", updatedMembers);
      return {
        ...prev,
        members: updatedMembers,
      };
    });
    setHasChanges(true);
  };

  const removeMember = (id) => {
    setBandData((prev) => ({
      ...prev,
      members: prev.members.filter((member) => member.id !== id),
    }));
    setHasChanges(true);
  };

  // Albums handlers
  const addAlbum = () => {
    console.log("Adding new album");
    const newAlbum = {
      id: Date.now(),
      title: "",
      year: new Date().getFullYear(),
      type: "Studio album",
      tracks: [],
    };
    console.log("New album:", newAlbum);
    setBandData((prev) => {
      const updatedDiscography = [...prev.discography, newAlbum];
      console.log("Updated discography:", updatedDiscography);
      return {
        ...prev,
        discography: updatedDiscography,
      };
    });
    setHasChanges(true);
  };

  const updateAlbum = (id, field, value) => {
    setBandData((prev) => ({
      ...prev,
      discography: prev.discography?.map((album) =>
        album.id === id ? { ...album, [field]: value } : album
      ),
    }));
    setHasChanges(true);
  };

  const removeAlbum = (id) => {
    setBandData((prev) => ({
      ...prev,
      discography: prev.discography.filter((album) => album.id !== id),
    }));
    setHasChanges(true);
  };

  // Tracks handlers
  const addTrack = (albumId) => {
    console.log(`Adding track to album ${albumId}`);
    const newTrack = {
      id: Date.now() + Math.random(),
      name: "",
      duration: "",
    };
    console.log("New track:", newTrack);
    setBandData((prev) => {
      const updatedDiscography = prev.discography.map((album) =>
        album.id === albumId
          ? { ...album, tracks: [...(album.tracks || []), newTrack] }
          : album
      );
      console.log(
        "Updated discography after adding track:",
        updatedDiscography
      );
      return {
        ...prev,
        discography: updatedDiscography,
      };
    });
    setHasChanges(true);
  };

  const updateTrack = (albumId, trackId, field, value) => {
    console.log(
      `Updating track ${trackId} in album ${albumId}, field: ${field}, value:`,
      value
    );
    setBandData((prev) => {
      const updatedDiscography = prev.discography.map((album) =>
        album.id === albumId
          ? {
              ...album,
              tracks: album.tracks.map((track) =>
                track.id === trackId ? { ...track, [field]: value } : track
              ),
            }
          : album
      );
      console.log("Updated discography:", updatedDiscography);
      return {
        ...prev,
        discography: updatedDiscography,
      };
    });
    setHasChanges(true);
  };

  const removeTrack = (albumId, trackId) => {
    setBandData((prev) => ({
      ...prev,
      discography: prev.discography.map((album) =>
        album.id === albumId
          ? {
              ...album,
              tracks: album.tracks.filter((track) => track.id !== trackId),
            }
          : album
      ),
    }));
    setHasChanges(true);
  };

  // Events handlers
  const addEvent = () => {
    const newEvent = {
      id: Date.now(),
      date: "",
      venue: "",
      city: "",
      country: "",
      ticketUrl: "",
      status: "available",
    };
    setBandData((prev) => ({
      ...prev,
      events: [...prev.events, newEvent],
    }));
    setHasChanges(true);
  };

  const updateEvent = (id, field, value) => {
    setBandData((prev) => ({
      ...prev,
      events: prev.events?.map((event) =>
        event.id === id ? { ...event, [field]: value } : event
      ),
    }));
    setHasChanges(true);
  };

  const removeEvent = (id) => {
    setBandData((prev) => ({
      ...prev,
      events: prev.events.filter((event) => event.id !== id),
    }));
    setHasChanges(true);
  };

  // Achievements handlers
  const addAchievement = () => {
    setBandData((prev) => ({
      ...prev,
      achievements: [
        ...prev.achievements,
        {
          id: Date.now(),
          title: "",
          year: new Date().getFullYear(),
        },
      ],
    }));
    setHasChanges(true);
  };

  const updateAchievement = (id, field, value) => {
    setBandData((prev) => ({
      ...prev,
      achievements: prev.achievements?.map((achievement) =>
        achievement.id === id ? { ...achievement, [field]: value } : achievement
      ),
    }));
    setHasChanges(true);
  };

  const removeAchievement = (id) => {
    setBandData((prev) => ({
      ...prev,
      achievements: prev.achievements.filter(
        (achievement) => achievement.id !== id
      ),
    }));
    setHasChanges(true);
  };

  const updateGalleryImage = (id, field, value) => {
    setBandData((prev) => ({
      ...prev,
      gallery: prev.gallery?.map((image) => {
        if (image.id === id) {
          if (field === "url" && value.startsWith("data:image/")) {
            // Ako je base64, čuvaj ga samo za preview, ne za slanje
            return {
              ...image,
              url: value, // Za preview
              pendingUpload: true, // Flag da je potreban upload
            };
          }
          return { ...image, [field]: value };
        }
        return image;
      }),
    }));
    setHasChanges(true);
  };

  const tabs = [
    { id: "biography", label: "Biografija", icon: FiMusic },
    { id: "members", label: "Članovi", icon: FiUsers },
    { id: "discography", label: "Diskografija", icon: FiDisc },
    { id: "events", label: "Koncerti", icon: FiCalendar },
    { id: "gallery", label: "Galerija", icon: FiImage },
    { id: "achievements", label: "Dostignuća", icon: FiAward },
    { id: "social", label: "Društvene mreže", icon: FiExternalLink },
  ];

  if (loading) {
    return (
      <AdminContainer>
        <PageContainer>
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <div className="spinner" />
            <p>Učitavam detalje benda...</p>
          </div>
        </PageContainer>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <PageHeader>
        <div>
          <BackLink
            onClick={() => {
              navigate("/admin-panel/bendovi");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
          >
            <FaArrowLeft /> Nazad u Bendove
          </BackLink>

          <PageTitle>{bandData.name} - Detalji</PageTitle>
        </div>

        <SaveButton
          onClick={handleSave}
          disabled={!hasChanges || loading}
          $hasChanges={hasChanges}
        >
          <FiSave />
          {loading ? "Čuvam..." : "Sačuvaj izmene"}
        </SaveButton>
      </PageHeader>

      {/* Tab Navigation */}
      <TabNavigation>
        {tabs?.map((tab) => {
          const Icon = tab.icon;
          return (
            <TabButton
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon />
              {tab.label}
            </TabButton>
          );
        })}
      </TabNavigation>

      {/* Tab Content */}
      <ContentSection>
        {activeTab === "biography" && (
          <Card>
            <CardHeader>
              <h3>Biografija benda</h3>
            </CardHeader>
            <CardContent>
              <FormGroup>
                <FormLabel>Biografija</FormLabel>
                <FormTextarea
                  rows={12}
                  value={bandData.biography}
                  onChange={(e) =>
                    handleInputChange("biography", e.target.value)
                  }
                  placeholder="Unesite detaljnu biografiju benda..."
                />
              </FormGroup>
            </CardContent>
          </Card>
        )}

        {activeTab === "members" && (
          <Card>
            <CardHeader>
              <h3>Članovi benda</h3>
              <Button onClick={addMember}>
                <FiPlus /> Dodaj člana
              </Button>
            </CardHeader>
            <CardContent>
              {bandData.members?.length === 0 ? (
                <EmptyState>
                  <FiUsers size={48} />
                  <h4>Nema dodanih članova</h4>
                  <p>Kliknite "Dodaj člana" da dodate prvog člana benda.</p>
                </EmptyState>
              ) : (
                bandData.members?.map((member, index) => (
                  <ItemCard key={member.id}>
                    <div className="item-header">
                      <h4>Član {index + 1}</h4>
                      <button
                        onClick={() => removeMember(member.id)}
                        className="remove-btn"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                    <FormGrid>
                      <FormGroup>
                        <FormLabel>Ime</FormLabel>
                        <FormInput
                          type="text"
                          value={member.name || ""}
                          onChange={(e) =>
                            updateMember(member.id, "name", e.target.value)
                          }
                          placeholder="Ime člana"
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Uloga</FormLabel>
                        <FormInput
                          type="text"
                          value={member.role || ""}
                          onChange={(e) =>
                            updateMember(member.id, "role", e.target.value)
                          }
                          placeholder="npr. Vokal, Gitara..."
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>U bendu od</FormLabel>
                        <FormInput
                          type="number"
                          min="1900"
                          max={new Date().getFullYear()}
                          value={member.joined || ""}
                          onChange={(e) =>
                            updateMember(
                              member.id,
                              "joined",
                              parseInt(e.target.value) ||
                                new Date().getFullYear()
                            )
                          }
                        />
                      </FormGroup>
                      <FormGroup className="full-width">
                        <FormLabel>Biografija</FormLabel>
                        <FormTextarea
                          rows={3}
                          value={member.bio || ""}
                          onChange={(e) =>
                            updateMember(member.id, "bio", e.target.value)
                          }
                          placeholder="Kratka biografija člana..."
                        />
                      </FormGroup>
                    </FormGrid>
                  </ItemCard>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "discography" && (
          <Card>
            <CardHeader>
              <h3>Diskografija</h3>
              <Button onClick={addAlbum}>
                <FiPlus /> Dodaj album
              </Button>
            </CardHeader>
            <CardContent>
              {bandData.discography?.length === 0 ? (
                <EmptyState>
                  <FiDisc size={48} />
                  <h4>Nema dodanih albuma</h4>
                  <p>Kliknite "Dodaj album" da dodate prvi album.</p>
                </EmptyState>
              ) : (
                bandData.discography?.map((album, index) => (
                  <ItemCard key={album.id}>
                    <div className="item-header">
                      <h4>Album {index + 1}</h4>
                      <button
                        onClick={() => removeAlbum(album.id)}
                        className="remove-btn"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>

                    <AlbumSection>
                      <FormGrid>
                        <FormGroup>
                          <FormLabel>Naziv albuma</FormLabel>
                          <FormInput
                            type="text"
                            value={album.title}
                            onChange={(e) =>
                              updateAlbum(album.id, "title", e.target.value)
                            }
                            placeholder="Naziv albuma"
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Godina</FormLabel>
                          <FormInput
                            type="number"
                            min="1900"
                            max={new Date().getFullYear() + 1}
                            value={album.year}
                            onChange={(e) =>
                              updateAlbum(
                                album.id,
                                "year",
                                parseInt(e.target.value) ||
                                  new Date().getFullYear()
                              )
                            }
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Tip</FormLabel>
                          <Relative>
                            <FormSelect
                              value={album.type}
                              onChange={(e) =>
                                updateAlbum(album.id, "type", e.target.value)
                              }
                            >
                              <option value="Studio album">Studio album</option>
                              <option value="Live album">Live album</option>
                              <option value="EP">EP</option>
                              <option value="Single">Single</option>
                              <option value="Compilation">Compilation</option>
                            </FormSelect>
                            <FiChevronDown
                              color="#ff6b35"
                              size={20}
                              style={{
                                position: "absolute",
                                right: 15,
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            />
                          </Relative>
                        </FormGroup>
                      </FormGrid>
                    </AlbumSection>

                    {/* Tracks section */}
                    {(album.type === "Studio album" ||
                      album.type === "Live album" ||
                      album.type === "EP") && (
                      <TracksSection>
                        <SectionDivider />
                        <div className="tracks-header">
                          <h5>Pesme na albumu</h5>
                          <Button
                            onClick={() => addTrack(album.id)}
                            style={{
                              fontSize: "0.85rem",
                              padding: "0.5rem 0.75rem",
                            }}
                          >
                            <FiPlus /> Dodaj pesmu
                          </Button>
                        </div>

                        {album.tracks?.length === 0 ? (
                          <EmptyState style={{ padding: "2rem 1rem" }}>
                            <FiMusic size={32} />
                            <h4 style={{ fontSize: "1rem" }}>
                              Nema dodanih pesama
                            </h4>
                            <p style={{ fontSize: "0.85rem" }}>
                              Kliknite "Dodaj pesmu" da dodate prvu pesmu na
                              album.
                            </p>
                          </EmptyState>
                        ) : (
                          <TracksList>
                            {album.tracks?.map((track, trackIndex) => (
                              <TrackItem key={track.id}>
                                <TrackGrid>
                                  <div className="track-number">
                                    <span>{trackIndex + 1}.</span>
                                  </div>
                                  <FormGroup>
                                    <FormLabel>Naziv pesme</FormLabel>
                                    <FormInput
                                      type="text"
                                      value={track.name || ""}
                                      onChange={(e) =>
                                        updateTrack(
                                          album.id,
                                          track.id,
                                          "name",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Naziv pesme"
                                    />
                                  </FormGroup>
                                  <FormGroup>
                                    <FormLabel>Trajanje</FormLabel>
                                    <FormInput
                                      type="text"
                                      value={track.duration || ""}
                                      onChange={(e) =>
                                        updateTrack(
                                          album.id,
                                          track.id,
                                          "duration",
                                          e.target.value
                                        )
                                      }
                                      placeholder="npr. 3:45"
                                    />
                                  </FormGroup>
                                  <TrackRemoveButton
                                    onClick={() =>
                                      removeTrack(album.id, track.id)
                                    }
                                    title="Ukloni pesmu"
                                  >
                                    <FiTrash2 />
                                  </TrackRemoveButton>
                                </TrackGrid>
                              </TrackItem>
                            ))}
                          </TracksList>
                        )}
                      </TracksSection>
                    )}
                  </ItemCard>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "events" && (
          <Card>
            <CardHeader>
              <h3>Predstojeci koncerti</h3>
              <Button onClick={addEvent}>
                <FiPlus /> Dodaj koncert
              </Button>
            </CardHeader>
            <CardContent>
              {bandData.events?.length === 0 ? (
                <EmptyState>
                  <FiCalendar size={48} />
                  <h4>Nema dodanih koncerata</h4>
                  <p>Kliknite "Dodaj koncert" da dodate novi koncert.</p>
                </EmptyState>
              ) : (
                bandData.events?.map((event, index) => (
                  <ItemCard key={event.id}>
                    <div className="item-header">
                      <h4>Koncert {index + 1}</h4>
                      <button
                        onClick={() => removeEvent(event.id)}
                        className="remove-btn"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                    <FormGrid>
                      <FormGroup>
                        <FormLabel>Datum</FormLabel>
                        <Relative>
                          <FormInput
                            type="date"
                            value={event.date}
                            onChange={(e) =>
                              updateEvent(event.id, "date", e.target.value)
                            }
                          />
                          <FaRegCalendarAlt
                            size={16}
                            color={"#ff6b35"}
                            style={{
                              position: "absolute",
                              right: ".8rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Relative>
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Mesto</FormLabel>
                        <FormInput
                          type="text"
                          value={event.venue}
                          onChange={(e) =>
                            updateEvent(event.id, "venue", e.target.value)
                          }
                          placeholder="Naziv mesta"
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Grad</FormLabel>
                        <FormInput
                          type="text"
                          value={event.city}
                          onChange={(e) =>
                            updateEvent(event.id, "city", e.target.value)
                          }
                          placeholder="Grad"
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Zemlja</FormLabel>
                        <FormInput
                          type="text"
                          value={event.country}
                          onChange={(e) =>
                            updateEvent(event.id, "country", e.target.value)
                          }
                          placeholder="Zemlja"
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Status</FormLabel>
                        <FormSelect
                          value={event.status}
                          onChange={(e) =>
                            updateEvent(event.id, "status", e.target.value)
                          }
                        >
                          <option value="available">Dostupno</option>
                          <option value="sold-out">Rasprodato</option>
                          <option value="cancelled">Otkazano</option>
                        </FormSelect>
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Link za karte</FormLabel>
                        <FormInput
                          type="url"
                          value={event.ticketUrl}
                          onChange={(e) =>
                            updateEvent(event.id, "ticketUrl", e.target.value)
                          }
                          placeholder="https://..."
                        />
                      </FormGroup>
                    </FormGrid>
                  </ItemCard>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "achievements" && (
          <Card>
            <CardHeader>
              <h3>Dostignuća</h3>
              <Button onClick={addAchievement}>
                <FiPlus /> Dodaj dostignuće
              </Button>
            </CardHeader>
            <CardContent>
              {bandData.achievements?.length === 0 ? (
                <EmptyState>
                  <FiAward size={48} />
                  <h4>Nema dodanih dostignuća</h4>
                  <p>Kliknite "Dodaj dostignuće" da dodate prvo dostignuće.</p>
                </EmptyState>
              ) : (
                <AchievementsList>
                  {bandData.achievements?.map((achievement) => (
                    <AchievementItem key={achievement.id}>
                      <div className="achievement-inputs">
                        <AchievementInput
                          type="text"
                          value={achievement.title}
                          onChange={(e) =>
                            updateAchievement(
                              achievement.id,
                              "title",
                              e.target.value
                            )
                          }
                          placeholder="Naziv dostignuća (npr. Prvi album, Grammy nagrada...)"
                        />
                        <AchievementYearInput
                          type="number"
                          value={achievement.year}
                          min="1900"
                          max={new Date().getFullYear()}
                          onChange={(e) =>
                            updateAchievement(
                              achievement.id,
                              "year",
                              parseInt(e.target.value)
                            )
                          }
                          placeholder="Godina"
                        />
                      </div>
                      <AchievementRemoveButton
                        onClick={() => removeAchievement(achievement.id)}
                        title="Ukloni dostignuće"
                      >
                        <FiTrash2 />
                      </AchievementRemoveButton>
                    </AchievementItem>
                  ))}
                </AchievementsList>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "gallery" && (
          <Card>
            <CardHeader>
              <h3>Foto galerija</h3>
              <Button onClick={addGalleryImage}>
                <FiPlus /> Dodaj sliku
              </Button>
            </CardHeader>
            <CardContent>
              {bandData.gallery?.length === 0 ? (
                <EmptyState>
                  <FiImage size={48} />
                  <h4>Nema dodanih slika</h4>
                  <p>Kliknite "Dodaj sliku" da dodate prvu sliku u galeriju.</p>
                </EmptyState>
              ) : (
                <GalleryGrid>
                  {bandData.gallery?.map((image, index) => (
                    <GalleryItemCard key={image.id}>
                      <GalleryItemHeader>
                        <GalleryItemControls>
                          <h4>Slika {index + 1}</h4>
                          {image.isMain && (
                            <MainImageBadge>Glavna slika</MainImageBadge>
                          )}
                          {image.isBanner && (
                            <MainImageBadge
                              style={{ backgroundColor: "#9b59b6" }}
                            >
                              Banner
                            </MainImageBadge>
                          )}
                        </GalleryItemControls>

                        <GalleryRemoveButton
                          type="button"
                          onClick={() => removeGalleryImage(image.id)}
                          title="Ukloni sliku"
                        >
                          <FiTrash2 size={16} />
                        </GalleryRemoveButton>
                      </GalleryItemHeader>

                      <ImagePreview>
                        {image.url ||
                        (image.file && typeof image.file === "object") ? (
                          <PreviewImage
                            src={
                              image.file && typeof image.file === "object"
                                ? URL.createObjectURL(image.file)
                                : image.url
                            }
                            alt="Band slika"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <PlaceholderImage
                          $hidden={
                            image.url ||
                            (image.file && typeof image.file === "object")
                          }
                        >
                          <FiImage size={30} />
                          <span>Nema slike</span>
                        </PlaceholderImage>
                      </ImagePreview>

                      <FormGrid>
                        <FormGroup className="full-width">
                          <FormLabel>Dodaj sliku</FormLabel>
                          <FileUploadInput
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                updateGalleryImage(image.id, "file", file);
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  updateGalleryImage(
                                    image.id,
                                    "url",
                                    event.target.result
                                  );
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </FormGroup>

                        <FormGroup className="full-width">
                          <CheckboxLabel>
                            <input
                              type="checkbox"
                              checked={image.isMain}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setMainImage(image.id);
                                } else {
                                  updateGalleryImage(image.id, "isMain", false);
                                }
                              }}
                            />
                            Postavi kao glavnu sliku benda
                          </CheckboxLabel>
                        </FormGroup>

                        <FormGroup className="full-width">
                          <CheckboxLabel>
                            <input
                              type="checkbox"
                              checked={image.isBanner}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBannerImage(image.id);
                                } else {
                                  updateGalleryImage(
                                    image.id,
                                    "isBanner",
                                    false
                                  );
                                }
                              }}
                            />
                            Postavi kao banner sliku
                          </CheckboxLabel>
                        </FormGroup>
                      </FormGrid>
                    </GalleryItemCard>
                  ))}
                </GalleryGrid>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "social" && (
          <Card>
            <CardHeader>
              <h3>Društvene mreže</h3>
            </CardHeader>
            <CardContent>
              <FormGrid>
                <FormGroup>
                  <FormLabel>Spotify</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.spotify}
                    onChange={(e) =>
                      handleInputChange(
                        "spotify",
                        e.target.value,
                        "socialLinks"
                      )
                    }
                    placeholder="https://spotify.com/..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>YouTube</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.youtube}
                    onChange={(e) =>
                      handleInputChange(
                        "youtube",
                        e.target.value,
                        "socialLinks"
                      )
                    }
                    placeholder="https://youtube.com/..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Instagram</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.instagram}
                    onChange={(e) =>
                      handleInputChange(
                        "instagram",
                        e.target.value,
                        "socialLinks"
                      )
                    }
                    placeholder="https://instagram.com/..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Facebook</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.facebook}
                    onChange={(e) =>
                      handleInputChange(
                        "facebook",
                        e.target.value,
                        "socialLinks"
                      )
                    }
                    placeholder="https://facebook.com/..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Twitter / X</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.twitter}
                    onChange={(e) =>
                      handleInputChange(
                        "twitter",
                        e.target.value,
                        "socialLinks"
                      )
                    }
                    placeholder="https://twitter.com/... ili https://x.com/..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Apple Music</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.apple}
                    onChange={(e) =>
                      handleInputChange("apple", e.target.value, "socialLinks")
                    }
                    placeholder="https://music.apple.com/..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>SoundCloud</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.soundcloud}
                    onChange={(e) =>
                      handleInputChange(
                        "soundcloud",
                        e.target.value,
                        "socialLinks"
                      )
                    }
                    placeholder="https://soundcloud.com/..."
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Website</FormLabel>
                  <FormInput
                    type="url"
                    value={bandData.socialLinks.website}
                    onChange={(e) =>
                      handleInputChange(
                        "website",
                        e.target.value,
                        "socialLinks"
                      )
                    }
                    placeholder="https://website.com/..."
                  />
                </FormGroup>
              </FormGrid>
            </CardContent>
          </Card>
        )}
      </ContentSection>
    </AdminContainer>
  );
};

export default AdminBandDetailsPage;
